import * as ct from "countries-and-timezones";

/**
 * IANA timezone ids for client-level display (backend stores the selected id).
 * UI lists major zones with current UTC offset (DST-aware via Intl).
 */

/** Widely used regions; sorted by offset at build time in getMajorTimezoneSelectOptions */
export const MAJOR_IANA_TIMEZONES = [
  "UTC",
  "Pacific/Midway",
  "Pacific/Honolulu",
  "America/Anchorage",
  "America/Los_Angeles",
  "America/Denver",
  "America/Chicago",
  "America/New_York",
  "America/Toronto",
  "America/Sao_Paulo",
  "Atlantic/Azores",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Africa/Cairo",
  "Europe/Moscow",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Asia/Bangkok",
  "Asia/Singapore",
  "Asia/Hong_Kong",
  "Asia/Tokyo",
  "Asia/Seoul",
  "Australia/Sydney",
  "Pacific/Auckland",
];

/**
 * Current offset from UTC for an IANA zone, e.g. "UTC+05:30", "UTC−08:00".
 * Uses Intl (reflects DST for "now").
 * @param {string} iana
 * @param {Date} [at] — defaults to now
 * @returns {string}
 */
export function getUtcOffsetLabel(iana, at = new Date()) {
  if (iana == null || String(iana).trim() === "") return "";
  const id = String(iana).trim();
  if (id === "UTC" || id === "Etc/UTC" || id === "Etc/GMT") {
    return "UTC±00:00";
  }
  try {
    const f = new Intl.DateTimeFormat("en-US", {
      timeZone: id,
      timeZoneName: "longOffset",
    });
    const part =
      f.formatToParts(at).find((p) => p.type === "timeZoneName")?.value || "";
    const m = part.match(/GMT([+-])(\d{1,2})(?::(\d{2}))?/i);
    if (m) {
      const sign = m[1] === "-" ? "-" : "+";
      const hours = String(parseInt(m[2], 10)).padStart(2, "0");
      const mins = m[3] != null ? String(parseInt(m[3], 10)).padStart(2, "0") : "00";
      return `UTC${sign}${hours}:${mins}`;
    }
    if (/^GMT$/i.test(part.trim())) {
      return "UTC±00:00";
    }
    return part ? part.replace(/^GMT/, "UTC") : id;
  } catch {
    return id;
  }
}

/**
 * Numeric offset minutes from UTC for sorting (DST-aware for `at`).
 * @param {string} iana
 * @param {Date} [at]
 * @returns {number}
 */
function getOffsetMinutesFromUtc(iana, at = new Date()) {
  if (!iana || String(iana).trim() === "") return 0;
  const id = String(iana).trim();
  if (id === "UTC" || id === "Etc/UTC" || id === "Etc/GMT") return 0;
  try {
    const f = new Intl.DateTimeFormat("en-US", {
      timeZone: id,
      timeZoneName: "longOffset",
    });
    const part =
      f.formatToParts(at).find((p) => p.type === "timeZoneName")?.value || "";
    const m = part.match(/GMT([+-])(\d{1,2})(?::(\d{2}))?/i);
    if (!m) return 0;
    const sign = m[1] === "+" ? 1 : -1;
    const h = parseInt(m[2], 10);
    const mins = m[3] != null ? parseInt(m[3], 10) : 0;
    return sign * (h * 60 + mins);
  } catch {
    return 0;
  }
}

function toTitleCaseWords(value) {
  return String(value || "")
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
}

function getFriendlyZoneLocation(iana) {
  const id = String(iana || "").trim();
  if (!id) return "";
  if (id === "UTC" || id === "Etc/UTC" || id === "Etc/GMT") return "UTC";
  const parts = id.split("/");
  if (parts.length <= 1) return id.replace(/_/g, " ");
  return parts
    .slice(1)
    .join(" / ")
    .replace(/_/g, " ")
    .replace(/\b\w+/g, toTitleCaseWords);
}

function getCountryNamesForTimezone(iana) {
  try {
    const tz = ct.getTimezone(iana);
    if (!tz || !Array.isArray(tz.countries) || tz.countries.length === 0) {
      return [];
    }
    return tz.countries
      .map((code) => ct.getCountry(code)?.name)
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));
  } catch {
    return [];
  }
}

/**
 * Major zones with labels: `Asia/Kolkata (UTC+05:30)`, sorted west → east by offset.
 * @param {Date} [at]
 * @returns {{ value: string, label: string }[]}
 */
export function getMajorTimezoneSelectOptions(at = new Date()) {
  const rows = MAJOR_IANA_TIMEZONES.map((value) => {
    const off = getUtcOffsetLabel(value, at);
    const countries = getCountryNamesForTimezone(value);
    const location = getFriendlyZoneLocation(value);
    const countryText = countries.length ? ` - ${countries.join(", ")}` : "";
    return {
      value,
      label: off ? `${off} - ${location}${countryText}` : `${location}${countryText}`,
      sortKey: getOffsetMinutesFromUtc(value, at),
    };
  });
  rows.sort((a, b) => a.sortKey - b.sortKey || a.value.localeCompare(b.value));
  return rows.map(({ value, label }) => ({ value, label }));
}

export function getDefaultBrowserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

/**
 * @deprecated Use getMajorTimezoneSelectOptions — kept for any stray imports
 */
export function getSortedTimezoneOptions() {
  return MAJOR_IANA_TIMEZONES.slice().sort();
}

/** Cached full list (IANA ids + offset labels), built once per page load. */
let allTimezoneSelectOptionsCache = null;

/**
 * All IANA timezones (when `Intl.supportedValuesOf` exists) with labels, sorted by offset.
 * Falls back to {@link getMajorTimezoneSelectOptions} in old runtimes.
 * @param {Date} [at]
 * @returns {{ value: string, label: string }[]}
 */
export function getAllTimezoneSelectOptions(at = new Date()) {
  if (allTimezoneSelectOptionsCache) {
    return allTimezoneSelectOptionsCache;
  }
  let ids = [];
  try {
    if (
      typeof Intl !== "undefined" &&
      typeof Intl.supportedValuesOf === "function"
    ) {
      ids = Intl.supportedValuesOf("timeZone");
    }
  } catch {
    /* ignore */
  }
  if (!ids.length) {
    allTimezoneSelectOptionsCache = getMajorTimezoneSelectOptions(at);
    return allTimezoneSelectOptionsCache;
  }
  const rows = ids.map((value) => {
    const off = getUtcOffsetLabel(value, at);
    const countries = getCountryNamesForTimezone(value);
    const location = getFriendlyZoneLocation(value);
    const countryText = countries.length ? ` - ${countries.join(", ")}` : "";
    return {
      value,
      label: off ? `${off} - ${location}${countryText}` : `${location}${countryText}`,
      offsetLabel: off || "UTC±00:00",
      sortKey: getOffsetMinutesFromUtc(value, at),
    };
  });
  rows.sort(
    (a, b) => a.sortKey - b.sortKey || a.value.localeCompare(b.value),
  );
  allTimezoneSelectOptionsCache = rows.map(({ value, label, offsetLabel }) => ({
    value,
    label,
    offsetLabel,
  }));
  return allTimezoneSelectOptionsCache;
}

/**
 * Group timezone options by current UTC offset.
 * Each group includes all timezone values as user-friendly text.
 * @param {Date} [at]
 * @returns {{ offsetLabel: string, zonesText: string, options: { value: string, label: string }[] }[]}
 */
export function getGroupedTimezoneSelectOptions(at = new Date()) {
  const options = getAllTimezoneSelectOptions(at);
  const groupedMap = new Map();
  options.forEach((opt) => {
    const key = opt.offsetLabel || "UTC±00:00";
    if (!groupedMap.has(key)) {
      groupedMap.set(key, []);
    }
    groupedMap.get(key).push(opt);
  });

  return Array.from(groupedMap.entries())
    .sort(([offsetA], [offsetB]) => {
      const parseOffset = (offset) => {
        const m = String(offset).match(/^UTC([+-])(\d{2}):(\d{2})$/);
        if (!m) return 0;
        const sign = m[1] === "+" ? 1 : -1;
        return sign * (parseInt(m[2], 10) * 60 + parseInt(m[3], 10));
      };
      return parseOffset(offsetA) - parseOffset(offsetB);
    })
    .map(([offsetLabel, groupOptions]) => ({
      offsetLabel,
      zonesText: groupOptions.map((item) => item.label).join(" | "),
      options: groupOptions.map(({ value, label }) => ({ value, label })),
    }));
}
